import React, { useState, useEffect } from "react";
import Spinner from "../common/Spinner";
import PropTypes from "prop-types";
import {
  usePostContractForm,
  updatePostContractForm,
  updateFieldErrors,
  updateFieldErrorsForSave,
} from "../../api/post-contract/PostContractFormAPI";
import { PostContractFormTable } from "./PostContractForm.functions";
import isEmpty from "../../validation/is-empty";
import { useBasicBenchmarkForm } from "../../api/benchmarking/FormBasedBenchmarkingAPI";
import { NestedDropDownComponent } from "../nested-drop-down/NestedDropDownComponent";

import "./PostContractForm.css";
import { CustomTooltip, CustomComponent } from "../common/Tooltip";
import { InformationTooltipHtml } from "./PostContractButtons.components";
import { BA_CBS_ROLE, hasRoles } from "../../utils/roles";

import {
  BenchmarkClientCompany,
  FileUpload,
  LocationsDropDown,
  ValidationMessage,
} from "./PostContractForm.components";

ConfigurableForm.propTypes = {
  projectId: PropTypes.string,
  selectedPackage: PropTypes.object,
  urlEndpoint: PropTypes.string,
  postContractFormType: PropTypes.string,
  setModal: PropTypes.func,
  stateUpdateFunction: PropTypes.func,
  benchmarkId: PropTypes.string,
  user: PropTypes.object,
};
export function ConfigurableForm(props) {
  const { benchmarkId } = props;
  const { projectId } = props;
  const { selectedPackage } = props;
  const { postContractFormType } = props;
  const { user } = props;

  let packageId = isEmpty(selectedPackage) ? "no id" : selectedPackage.Id;

  const { formData } = useBasicBenchmarkForm(postContractFormType, benchmarkId);

  const { postContractForm } = usePostContractForm(
    postContractFormType,
    projectId,
    packageId
  );

  if (formData.loading && postContractForm.loading) {
    return <Spinner />;
  }
  return (
    <>
      {!isEmpty(formData.data) && (
        <GenericForm formData={formData} {...props} user={user} />
      )}
      {!isEmpty(postContractForm.data) && (
        <GenericForm formData={postContractForm} {...props} user={user} />
      )}
    </>
  );
}

GenericForm.propTypes = {
  formData: PropTypes.object,
  urlEndpoint: PropTypes.string,
  postContractFormType: PropTypes.string,
  setModal: PropTypes.func,
  stateUpdateFunction: PropTypes.func,
  hideDeleteAdd: PropTypes.bool,
  parentErrors: PropTypes.object,
  user: PropTypes.object,
  selectedBenchmarks: PropTypes.object,
};
export function GenericForm(props) {
  const { urlEndpoint } = props;
  const { postContractFormType } = props;
  const { setModal } = props;
  const { stateUpdateFunction } = props;

  const { formData } = props;
  const { hideDeleteAdd } = props;
  const { parentErrors } = props;
  const [fields, setFields] = useState([]);
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const { user } = props;
  const { selectedBenchmarks } = props;

  const basicBm = !isEmpty(selectedBenchmarks) ? selectedBenchmarks[0] : {};
  const isAdmin = hasRoles(user.roles, BA_CBS_ROLE);
  const isPublished = String(basicBm.status).toLowerCase() === "published";
  const allowedTypes = [
    "basicbenchmarkeditprojectfields",
    "elementaluploadfields",
    "metricsfields",
    "carbonanalysisuploadfields",
    "notesfields",
    "elementeditform",
  ];

  const type = postContractFormType?.toLowerCase();
  let canShowSaveButton = true;
  if (type && allowedTypes.includes(type)) {
    canShowSaveButton = !(isPublished && !isAdmin);
  }

  useEffect(() => {
    if (!isEmpty(parentErrors)) {
      setErrors(parentErrors);
    }
    const fields = [];
    formData?.data?.[postContractFormType]?.forEach((field) => {
      // Set default dropdown value
      if (field.dataType === "dropdown" && field.value === "") {
        field.value = isEmpty(field.options[0]) ? "" : field.options[0].value;
      }

      fields.push(field);
    });

    setFields(fields);

    if (!isEmpty(parentErrors)) {
      updateFieldErrors(fields, setFieldErrors);
    }
  }, [formData.data, postContractFormType, parentErrors]);

  if (formData.loading && !isEmpty(errors)) {
    return <Spinner />;
  }

  const display = [];
  // Add a random query value to prevent caching
  const randomQueryValue = "?" + Date.now();

  fields.forEach((field) => {
    let textValue = fields.find((f) => f.id === field.id).value;

    if (textValue === null) {
      textValue = "";
    }
    //moved each conditional code to a serperate function to reduce Congnitive Complexity reported by Sonarqube
    if (field.dataType === "text") {
      generateTextField(
        display,
        field,
        fieldErrors,
        textValue,
        fields,
        setFields
      );
    }

    if (field.dataType === "textarea") {
      generateTextAreaField(
        display,
        field,
        fieldErrors,
        textValue,
        fields,
        setFields
      );
    }

    if (field.dataType === "date") {
      generateDateField(
        display,
        field,
        fieldErrors,
        textValue,
        fields,
        setFields
      );
    }

    if (field.dataType === "dropdown") {
      generateDropdownField(display, field, fieldErrors, fields, setFields);
    }

    if (field.dataType === "locationDropDown") {
      generateLocationDropdownField(
        display,
        field,
        fields,
        fieldErrors,
        setFields,
        setSaveDisabled
      );
    }

    if (field.dataType === "nestedDropDown") {
      generateNestedDropdownField(
        display,
        field,
        fieldErrors,
        fields,
        setFields
      );
    }

    if (field.dataType === "file") {
      generateFileField(
        display,
        field,
        fields,
        fieldErrors,
        setFields,
        setFieldErrors
      );
    }
    if (field.dataType === "downloadButton") {
      generateDownLoadButtonField(display, field, randomQueryValue);
    }
    if (field.dataType === "dynamicDownloadButton") {
      generateDynamicDownloadButtonField(
        fields,
        field,
        randomQueryValue,
        display
      );
    }

    if (field.dataType === "checkbox") {
      generateCheckboxField(field, display, fields, setFields);
    }

    if (field.dataType === "client-modal-component") {
      generateClientField(display, field, fieldErrors, fields, setFields);
    }

    if (field.dataType === "table") {
      display.push(
        <PostContractFormTable
          key={field.id}
          field={field}
          fields={fields}
          setFields={setFields}
          hideDeleteAdd={hideDeleteAdd}
          fieldErrors={fieldErrors}
          setSaveDisabled={setSaveDisabled}
        />
      );
    }
  });

  return (
    <div className="pcf-container">
      {display}
      <div className="general-row-container">
        <div className="pcu-error-container">
          <div className={errors.type}>
            <b>{errors.text}</b>
          </div>
        </div>
      </div>

      <div className="general-row-container">
        {canShowSaveButton && (
          <button
            disabled={saveDisabled}
            id={"pcf-table-save-button"}
            className="general-upload-button"
            onClick={async () => {
              let errorsLst = await updateFieldErrorsForSave(
                fields,
                setFieldErrors
              );

              const data = {
                fields,
                setErrors,
                urlEndpoint,
                setModal,
                stateUpdateFunction,
                setFieldErrors,
              };

              if (errorsLst.length > 0) {
                setErrors({
                  text: "Please Check Field(s)",
                  type: "feedback-error",
                });
              } else {
                await updatePostContractForm(data);
              }
            }}
          >
            {!saveDisabled ? "Save" : "Please Wait.."}
          </button>
        )}
      </div>
    </div>
  );
}

function generateCheckboxField(field, display, fields, setFields) {
  let checkboxValue = false;
  if (
    field.value === "true" ||
    field.value === "True" ||
    field.value === true
  ) {
    checkboxValue = true;
  }
  if (
    field.value === "false" ||
    field.value === "False" ||
    field.value === false
  ) {
    checkboxValue = false;
  }
  display.push(
    <label key={field.id} className="checkboxes-vertical-container">
      <div className="checkbox-label">{field.name}</div>
      <input
        id={"pcf-checkbox-" + field.id}
        type="checkbox"
        name={field.name}
        value={field.name}
        checked={checkboxValue}
        onChange={(e) => {
          const checked = e.target.checked;

          let temp = [...fields];
          temp.find((f) => f.id === field.id).value = checked;
          setFields(temp);
        }}
      />
      <span className="checkmark"></span>
    </label>
  );
}

function generateDynamicDownloadButtonField(
  fields,
  field,
  randomQueryValue,
  display
) {
  const optedValue = () => {
    const depField = fields.find(
      (f) => f.key === field.key && f.id !== field.id
    );
    const selectedValue = document.querySelector(
      `select[name="${depField.name}"]`
    )?.value;
    return selectedValue;
  };

  let valueSelected = optedValue();

  const onButtonClick = () => {
    const selectedValue = valueSelected;
    if (selectedValue) {
      const fileUrl = field.value.replace("{PLACEHOLDER}", selectedValue);
      window.open(fileUrl + "?" + randomQueryValue, "_blank");
    }
  };
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <div className="pcf-element-item">{field.name}</div>
        <div className="pcf-element-item">
          <div>
            <input
              className={
                isEmpty(valueSelected)
                  ? "general-modal-button disabled"
                  : "general-modal-button"
              }
              type="button"
              value={field.placeholderText}
              onClick={onButtonClick}
              disabled={isEmpty(valueSelected)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function generateDownLoadButtonField(display, field, randomQueryValue) {
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <div className="pcf-element-item">{field.name}</div>
        <div className="pcf-element-item">
          <div>
            <input
              className="general-modal-button"
              type="button"
              value={field.placeholderText}
              onClick={(e) => {
                window.open(field.value + "?" + randomQueryValue, "_blank");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function generateFileField(
  display,
  field,
  fields,
  fieldErrors,
  setFields,
  setFieldErrors
) {
  display.push(
    <FileUpload
      key={field.id}
      field={field}
      fields={fields}
      fieldErrors={fieldErrors}
      setFields={setFields}
      setFieldErrors={setFieldErrors}
    />
  );
}

function generateNestedDropdownField(
  display,
  field,
  fieldErrors,
  fields,
  setFields
) {
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <div className="pcf-element-item">{field.name}</div>
        <ValidationMessage field={field} fieldErrors={fieldErrors} />
      </div>
      <NestedDropDownComponent
        data={field.optons}
        label={"Entity"}
        width={"300px"}
        onSelect={(value) => {
          let temp = [...fields];
          temp.find((f) => f.id === field.id).value = value;
          setFields(temp);
        }}
      ></NestedDropDownComponent>
    </div>
  );
}

function generateLocationDropdownField(
  display,
  field,
  fields,
  fieldErrors,
  setFields,
  setSaveDisabled
) {
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <LocationsDropDown
          field={field}
          fields={fields}
          fieldErrors={fieldErrors}
          setFields={setFields}
          setSaveDisabled={setSaveDisabled}
        />
      </div>
    </div>
  );
}

function generateDropdownField(display, field, fieldErrors, fields, setFields) {
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <div>
          <div className="pcf-element-item">
            {field.name}
            {!isEmpty(field.toolTip) && (
              <CustomTooltip
                tooltipHtml={<CustomComponent htmlContent={field.toolTip} />}
                tooltipIcon={
                  <InformationTooltipHtml key={"tooltip-" + field.id} />
                }
                iconText={field.name}
              />
            )}
          </div>
          <ValidationMessage field={field} fieldErrors={fieldErrors} />
        </div>
        <select
          id={"pcf-dropdown-" + field.id}
          name={field.name}
          className="pcf-element-item"
          value={fields.find((f) => f.id === field.id).value}
          onChange={(e) => {
            let temp = [...fields];
            temp.find((f) => f.id === field.id).value = e.target.value;
            setFields(temp);
          }}
        >
          {field?.options?.map((option) => {
            return (
              <option key={option.id} value={option.value}>
                {option.name}
              </option>
            );
          })}
        </select>
      </div>
    </div>
  );
}

function generateDateField(
  display,
  field,
  fieldErrors,
  textValue,
  fields,
  setFields
) {
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <div>
          <div className="pcf-element-item">{field.name}</div>
          <ValidationMessage field={field} fieldErrors={fieldErrors} />
        </div>

        <input
          id={"pcf-date-" + field.id}
          type="date"
          className="pcf-date-item"
          value={textValue}
          placeholder={field.placeholderText}
          onChange={(e) => {
            let temp = [...fields];
            temp.find((f) => f.id === field.id).value = e.target.value;
            setFields(temp);
          }}
          required
        ></input>
      </div>
    </div>
  );
}

function generateTextAreaField(
  display,
  field,
  fieldErrors,
  textValue,
  fields,
  setFields
) {
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <div>
          <div className="pcf-element-item">{field.name}</div>
          <ValidationMessage field={field} fieldErrors={fieldErrors} />
        </div>

        <textarea
          id={"pcf-textarea-" + field.id}
          className="pcf-element-item"
          value={textValue}
          placeholder={field.placeholderText}
          onChange={(e) => {
            let temp = [...fields];
            temp.find((f) => f.id === field.id).value = e.target.value;
            setFields(temp);
          }}
        ></textarea>
      </div>
    </div>
  );
}

function generateTextField(
  display,
  field,
  fieldErrors,
  textValue,
  fields,
  setFields
) {
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <div>
          <div className="pcf-element-item">{field.name}</div>
          <ValidationMessage field={field} fieldErrors={fieldErrors} />
        </div>

        <input
          id={"pcf-text-" + field.id}
          className="pcf-element-item"
          value={textValue}
          placeholder={field.placeholderText}
          onChange={(e) => {
            let temp = [...fields];
            temp.find((f) => f.id === field.id).value = e.target.value;
            setFields(temp);
          }}
        ></input>
      </div>
    </div>
  );
}

function generateClientField(display, field, fieldErrors, fields, setFields) {
  display.push(
    <div className="general-row-container" key={field.id}>
      <div className="pcf-element">
        <div>
          <div className="pcf-element-item">{field.name}</div>
          <ValidationMessage field={field} fieldErrors={fieldErrors} />
        </div>
        <div className="pcf-element-item">
          <BenchmarkClientCompany
            field={field}
            fields={fields}
            setFields={setFields}
          />
        </div>
      </div>
    </div>
  );
}
